import { FC, useContext } from "react";
import clsx from "clsx";

import ThemeContext from "~/contexts/ThemeContext";
import TaxContext from "~/contexts/TaxContext";
import { DealGroup as DealGroupData } from "~/helpers/bundleDisplay";
import { getModifiersList } from "~/helpers/cartHelpers";
import { offerSavingColour } from "~/helpers/offerTheme";

import { CartLine } from "~/entities/orderEntities";
import CloseIcon from "~/icons/CloseIcon";

import styles from "./DealGroup.module.css";

type DealGroupProps = {
  deal: DealGroupData;
  readOnly?: boolean;
  onRemove?: VoidFunction;
  onEdit?: VoidFunction;
  onRemoveMember?: (line: CartLine) => void;
};

// One bundle deal in the cart: name and total (deal price + extras), its items as "Included" with only extras
// priced, and the saving
const DealGroup: FC<DealGroupProps> = ({
  deal,
  readOnly,
  onRemove,
  onEdit,
  onRemoveMember,
}) => {
  const { template } = useContext(ThemeContext);
  const { formatAmount } = useContext(TaxContext);

  return (
    <section
      className={clsx(styles.group, readOnly && styles.readOnly)}
      aria-label={deal.name}
    >
      <header
        className={styles.header}
        style={{
          backgroundColor: template.cart.offer.backgroundColour,
          color: template.cart.offer.fontColour,
        }}
      >
        <span className={styles.name}>{deal.name}</span>
        <span
          className={styles.total}
          style={{ color: template.cart.item.priceFontColour }}
        >
          {formatAmount(deal.total)}
        </span>
      </header>
      <ul className={styles.members}>
        {deal.members.map(({ line, extras }) => {
          const modifiers = getModifiersList(line.item);
          return (
            <li key={line.id} className={styles.member}>
              <span className={styles.memberText}>
                <span className={styles.memberName}>
                  {line.item.qty > 1 && `${line.item.qty} × `}
                  {line.item.product.name}
                </span>
                {modifiers && (
                  <span className={styles.secondary}>{modifiers}</span>
                )}
              </span>
              <span className={clsx(styles.secondary, styles.memberPrice)}>
                <span>Included</span>
                {extras > 0 && <span>+{formatAmount(extras)}</span>}
              </span>
              {!readOnly && onRemoveMember && (
                <button
                  type="button"
                  className={styles.removeMember}
                  aria-label={`Remove ${line.item.product.name}`}
                  onClick={() => onRemoveMember(line)}
                >
                  <CloseIcon />
                </button>
              )}
            </li>
          );
        })}
      </ul>
      <footer className={styles.footer}>
        <span style={{ color: offerSavingColour(template) }}>
          You save {formatAmount(deal.saving)}
        </span>
        {!readOnly && (onRemove || onEdit) && (
          <span className={styles.actions}>
            {onEdit && (
              <button type="button" className={styles.action} onClick={onEdit}>
                Edit
              </button>
            )}
            {onRemove && (
              <button
                type="button"
                className={styles.action}
                onClick={onRemove}
              >
                Remove
              </button>
            )}
          </span>
        )}
      </footer>
    </section>
  );
};

export default DealGroup;
